import { Link } from 'react-router-dom';
import './LegalPage.css';

const EFFECTIVE_DATE = 'August 13, 2026';

function PrivacyPolicy() {
  const backgroundImage = '/background.jpg';

  return (
    <div className="legal-page" style={{ backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundRepeat: 'no-repeat', backgroundPosition: 'center center' }}>
      <div className="legal-content">
        <Link to="/" className="back-link">← Back to Home</Link>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Effective Date: {EFFECTIVE_DATE}</p>

        <section>
          <h2>1. Scope and Controller</h2>
          <p>
            1.1 This Privacy Policy governs the processing of Personal Information by tnysocial LLC, a
            limited liability company organised under the laws of the State of Delaware, United States
            of America (the "Company"), in connection with the mobile software applications published
            by the Company (each, the "Application"). The Application presently published is Last One
            Standing.
          </p>
          <p>
            1.2 The Company is the controller in respect of the processing described herein, and is the
            business to which requests under United States state privacy legislation are to be directed.
          </p>
          <p>
            1.3 This Privacy Policy is incorporated into the{' '}
            <Link to="/terms">Terms and Conditions</Link> and forms part thereof.
          </p>
          <p>
            1.4 Requests, enquiries and notices under this Privacy Policy are to be directed to{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>.
          </p>
        </section>

        <section>
          <h2>2. Definitions</h2>
          <p>
            2.1 "Anonymous Identifier" means the randomly generated identifier provisioned to the
            device upon first launch of the Application by means of anonymous authentication, and by
            reference to which the processing described herein is carried out.
          </p>
          <p>
            2.2 "Processor" means a third party which processes Personal Information on behalf of and
            upon the documented instructions of the Company, and includes a "service provider" or
            "contractor" as those terms are defined under the California Consumer Privacy Act.
          </p>
        </section>

        <section>
          <h2>3. Absence of Registered Accounts</h2>
          <p>
            3.1 The Application does not provide for registered accounts. The Company does not
            solicit, require, collect or hold any name, electronic mail address, telephone number,
            postal address, date of birth, payment card number or other direct identifier.
          </p>
          <p>
            3.2 Authentication is anonymous. The Anonymous Identifier is not derived from the User, the
            device, or any account of the User with any third party, and is not used by the Company to
            identify any natural person.
          </p>
          <p>
            3.3 The Company does not employ Sign in with Apple or any other identity provider.
          </p>
        </section>

        <section>
          <h2>4. Categories of Personal Information Processed</h2>
          <p>
            4.1 The Application does not accept text or other content submitted by the User. No
            information is provided directly by the User in the ordinary course of use. Where a User
            corresponds with the Company, the Company processes the contents of such correspondence and
            the address from which it is sent.
          </p>
          <p>
            4.2 The following categories are processed automatically upon use of the Application:
          </p>
          <ul>
            <li>(a) the Anonymous Identifier;</li>
            <li>(b) device and application characteristics, comprising device model, operating system
            version, application version, language, time zone and network type;</li>
            <li>(c) usage and gameplay data, comprising puzzle identifiers, outcomes, elapsed times,
            remaining lives, streaks, accrual and expenditure of virtual items, screens viewed and
            interactions with interface elements;</li>
            <li>(d) session recordings, as further described at Article 6;</li>
            <li>(e) diagnostic data, comprising crash reports, error traces and a sample of performance
            traces;</li>
            <li>(f) advertising identifiers, comprising the Identifier for Advertisers, processed only
            in the circumstances described at Article 7;</li>
            <li>(g) subscription status and the corresponding transaction receipt issued by Apple Inc.;</li>
            <li>(h) internet protocol addresses, received by the Company and its Processors as an
            incident of network communication, from which approximate geographic location at the level
            of country or region may be inferred; and</li>
            <li>(i) daily completion times, comprising the elapsed duration recorded upon completion of
            a daily puzzle, together with the date and the Anonymous Identifier.</li>
          </ul>
          <p>
            4.3 The Company does not process precise geolocation data, contacts, photographs, calendar
            entries, camera or microphone input, biometric information, or any special category of
            personal data.
          </p>
        </section>

        <section>
          <h2>5. Purposes of Processing and Legal Bases</h2>
          <p>
            5.1 The categories identified at Article 4 are processed for the following purposes: the
            provision and operation of the Application; the retention and restoration of progression
            between sessions; the computation of comparative statistics; the administration of
            subscription entitlements; the display of advertising; the detection, prevention and
            investigation of fraud, abuse and manipulation of results; the diagnosis and correction of
            defects; the measurement and improvement of the Application; and compliance with legal
            obligations to which the Company is subject.
          </p>
          <p>
            5.2 Where the UK GDPR or the EU GDPR applies, the Company relies upon the following legal
            bases:
          </p>
          <ul>
            <li>(a) performance of a contract, in respect of the operation of the Application, the
            retention of progression, and the provision of subscription entitlements;</li>
            <li>(b) consent, in respect of the use of advertising identifiers and personalised
            advertising, obtained in the manner described at Article 7; and</li>
            <li>(c) legitimate interests, in respect of analytics, session recording, diagnostic
            reporting and the prevention of fraud, such interests being the maintenance, security and
            improvement of the Application. The right to object as described at Section 14.1 applies to
            processing carried out upon this basis.</li>
          </ul>
          <p>
            5.3 The Company does not take decisions based solely upon automated processing which
            produce legal effects concerning the User or which similarly significantly affect the User
            within the meaning of Article 22 of the UK GDPR and the EU GDPR. Personalised advertising
            is carried out as described at Article 7.
          </p>
        </section>

        <section>
          <h2>6. Session Recording</h2>
          <p>
            6.1 The Application records sessions by means of the analytics Processor identified at
            Section 7.1. Such recordings comprise the screens displayed, the sequence in which they were
            displayed, and the position and timing of interactions with the interface.
          </p>
          <p>
            6.2 The Application contains no text input fields. No keystroke or typed input is captured.
          </p>
          <p>
            6.3 Recordings are confined to the Application. No other application, system interface,
            notification or content outside the Application is captured.
          </p>
          <p>
            6.4 Recordings are associated with the Anonymous Identifier.
          </p>
          <p>
            6.5 The Application does not presently provide an in-application facility to disable
            analytics or session recording. Requests to cease such processing or to erase recordings are
            to be directed to <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>.
          </p>
        </section>

        <section>
          <h2>7. Advertising, Tracking and Device Identifiers</h2>
          <p>
            7.1 Advertising is served by Google AdMob. A single interstitial advertisement is displayed
            following an unsuccessful daily puzzle. The Application does not display banner or rewarded
            advertising. No advertising is displayed to Users holding a subsisting premium subscription.
          </p>
          <p>
            7.2 Upon first launch, the operating system presents the App Tracking Transparency
            authorisation request. Where authorisation is granted, the Identifier for Advertisers may be
            processed for the purposes of personalised advertising and the measurement thereof. Where
            authorisation is declined or not granted, the Identifier for Advertisers is not made
            available and advertising is requested on a non-personalised basis.
          </p>
          <p>
            7.3 Authorisation under Section 7.2 may be granted or withdrawn at any time in the settings
            of the operating system, under Privacy and Security, Tracking.
          </p>
          <p>
            7.4 Where the User is located in the United Kingdom or the European Economic Area, consent
            is additionally obtained by means of the Google User Messaging Platform prior to any
            advertising request, and the elections recorded therein are given effect.
          </p>
          <p>
            7.5 Attribution is performed by means of Apple SKAdNetwork. SKAdNetwork operates
            irrespective of the authorisation described at Section 7.2, does not employ the Identifier
            for Advertisers, and does not identify the User individually.
          </p>
        </section>

        <section>
          <h2>8. Payment and Subscription Information</h2>
          <p>
            8.1 All payments are processed by Apple Inc. The Company does not receive, process, hold or
            have access to any payment card number, billing address or authentication credential.
          </p>
          <p>
            8.2 The Company receives the transaction receipt confirming subscription status. Such
            receipt, together with the Anonymous Identifier, is transmitted to the subscription
            management Processor identified at Section 9.1 for the purpose of conferring the
            corresponding entitlement.
          </p>
        </section>

        <section>
          <h2>9. Recipients and Processors</h2>
          <p>
            9.1 The Company discloses Personal Information to the following categories of recipient,
            each for the purpose stated and pursuant to contractual terms restricting its use:
          </p>
          <ul>
            <li>(a) Google LLC, in respect of Firebase, for anonymous authentication and for storage of
            daily completion times and an associated server timestamp.{' '}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(b) Google LLC, in respect of AdMob and the User Messaging Platform, for the delivery of
            advertising and the administration of advertising consent.{' '}
            <a href="https://business.safety.google/privacy/" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(c) PostHog, Inc., for product analytics and session recording.{' '}
            <a href="https://posthog.com/privacy" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(d) Functional Software, Inc., trading as Sentry, for diagnostic and error reporting.{' '}
            <a href="https://sentry.io/privacy/" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(e) RevenueCat, Inc., for the administration of subscription entitlements.{' '}
            <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(f) Superwall, Inc., for the presentation and measurement of subscription offers.{' '}
            <a href="https://superwall.com/privacy" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(g) Expo, Inc., for the delivery of application updates.{' '}
            <a href="https://expo.dev/privacy" target="_blank" rel="noreferrer">Policy</a></li>
            <li>(h) Apple Inc., in respect of distribution, payment processing and attribution.{' '}
            <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer">Policy</a></li>
          </ul>
          <p>
            9.2 The Company does not disclose Personal Information to data brokers and does not
            otherwise disclose Personal Information save as provided in this Article 9 or as follows:
            (a) where required by Applicable Law or by valid legal process; (b) where reasonably
            necessary to investigate suspected fraud or to establish, exercise or defend legal claims;
            (c) where reasonably necessary to protect the rights, property or safety of the Company, its
            Users or the public; and (d) to an acquirer or successor in connection with any merger,
            acquisition, reorganisation or sale of assets, in which event notice shall be given by
            revision of this page prior to any such transfer taking effect.
          </p>
        </section>

        <section>
          <h2>10. Notifications</h2>
          <p>
            10.1 Reminder notifications are scheduled locally upon the device. The Company does not
            operate a push notification service and does not collect, receive or hold any push
            notification token. No transmission to the Company occurs in connection with the scheduling
            or delivery of a notification, and the Company has no means of determining whether any
            notification was delivered.
          </p>
          <p>
            10.2 The Company records, by reference to the Anonymous Identifier, the fact that the
            Application was opened by means of a notification.
          </p>
          <p>
            10.3 Notifications may be disabled within the Application or within the settings of the
            operating system.
          </p>
        </section>

        <section>
          <h2>11. Information Retained Locally</h2>
          <p>
            11.1 Progression, streaks, virtual item balances, preferences, onboarding state and any
            puzzle in progress are recorded in local storage upon the device and are not transmitted to
            the Company.
          </p>
          <p>
            11.2 Information retained pursuant to Section 11.1 is erased upon deletion of the
            Application.
          </p>
        </section>

        <section>
          <h2>12. Retention, Erasure and Limitations Thereupon</h2>
          <p>
            12.1 Personal Information is retained for so long as is necessary for the purposes
            identified at Article 5 or for such longer period as Applicable Law requires. Daily
            completion times are retained for the purpose of comparative computation. Analytics and
            diagnostic data are retained in accordance with the standard retention periods of the
            respective Processors.
          </p>
          <p>
            12.2 The Application provides an erasure facility within its settings, designated "Delete My
            Data". Operation of that facility erases the daily completion times and the server timestamp
            recorded against the Anonymous Identifier, deletes the anonymous authentication identity,
            and erases the information retained locally pursuant to Article 11.
          </p>
          <p>
            12.3 The facility described at Section 12.2 does not extend to Personal Information already
            transmitted to the Processors identified at Sections 9.1(c) to 9.1(f). Erasure of such
            information is to be requested at{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>, and the Company shall procure
            such erasure so far as it is able.
          </p>
          <p>
            12.4 Transaction records required to be retained for taxation, accounting or audit purposes
            are not susceptible of erasure upon request.
          </p>
        </section>

        <section>
          <h2>13. Minors</h2>
          <p>
            13.1 The Application is intended for a general audience aged thirteen (13) years and above.
            It is not directed to children under the age of thirteen (13) years, and the Company does not
            knowingly collect Personal Information from such children. In certain jurisdictions a higher
            minimum age applies, being sixteen (16) years in certain Member States of the European Union.
          </p>
          <p>
            13.2 A parent or legal guardian who believes that a child under the age of thirteen (13)
            years has provided Personal Information to the Company may notify{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>, whereupon such information
            shall be erased.
          </p>
          <p>
            13.3 The Company does not knowingly sell or share the Personal Information of any person
            under the age of sixteen (16) years.
          </p>
        </section>

        <section id="do-not-sell">
          <h2>14. United States State Privacy Rights</h2>
          <p>
            14.1 Residents of California, Colorado, Connecticut, Virginia and other states having
            comprehensive privacy legislation are entitled, subject to the conditions and exceptions
            prescribed by the applicable statute, to know the categories and specific items of Personal
            Information processed; to obtain a copy thereof; to require correction of inaccuracies; to
            require deletion; to opt out of targeted advertising, sale and sharing; and not to be
            subjected to discriminatory treatment by reason of the exercise of any such right.
          </p>
          <p>
            14.2 The Company does not sell Personal Information for monetary consideration and has not
            done so. The Company gives notice that the processing of advertising identifiers for the
            purposes of personalised advertising described at Article 7 may constitute "sharing for
            cross-context behavioural advertising" within the meaning of the California Consumer Privacy
            Act as amended by the California Privacy Rights Act.
          </p>
          <p>
            14.3 The following mechanisms operate to opt out of the processing described at Section 14.2:
          </p>
          <ul>
            <li>(a) declining the App Tracking Transparency authorisation request described at Section
            7.2, or withdrawing such authorisation in the settings of the operating system;</li>
            <li>(b) acquiring a premium subscription, whereupon advertising ceases to be displayed; and</li>
            <li>(c) delivering a request to{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>.</li>
          </ul>
          <p>
            14.4 Opt-out preference signals, including Global Privacy Control, are given effect upon this
            website. The Application, not being a browser, gives effect to the operating system
            mechanism identified at Section 14.3(a).
          </p>
          <p>
            14.5 Requests under this Article 14 are to be delivered to{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a> and shall be responded to within
            forty-five (45) days, subject to any permitted extension. The Company holds no name,
            electronic mail address or other direct identifier by reference to which a requesting person
            may be verified, and may accordingly require the Anonymous Identifier, which is displayed
            within the settings of the Application, in order to locate the relevant records. Where the
            Company is unable to associate the information sought with the requesting person, it shall
            so inform that person and shall decline the request.
          </p>
          <p>
            14.6 An authorised agent may submit a request upon proof of authorisation.
          </p>
        </section>

        <section>
          <h2>15. United Kingdom and European Economic Area Rights</h2>
          <p>
            15.1 Data subjects in the United Kingdom and the European Economic Area are entitled,
            subject to the conditions and exceptions prescribed by the UK GDPR and the EU GDPR
            respectively, to access their personal data; to obtain rectification of inaccurate data; to
            obtain erasure; to obtain restriction of processing; to data portability; to object to
            processing carried out upon the basis of legitimate interests; and to withdraw consent at any
            time, such withdrawal not affecting the lawfulness of processing carried out prior thereto.
          </p>
          <p>
            15.2 Requests under this Article 15 are to be delivered to{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>. Section 14.5 applies to the
            verification of such requests.
          </p>
          <p>
            15.3 A data subject is entitled to lodge a complaint with the supervisory authority of the
            Member State of habitual residence, place of work or place of the alleged infringement, and,
            in the United Kingdom, with the Information Commissioner's Office.
          </p>
        </section>

        <section>
          <h2>16. International Transfers</h2>
          <p>
            16.1 The Company is established in the United States of America. Personal Information is
            processed in the United States of America and in such other territories as the Processors
            identified at Article 9 may operate.
          </p>
          <p>
            16.2 Where Personal Information originating in the United Kingdom or the European Economic
            Area is transferred to a third country, the Company relies upon the transfer mechanisms
            incorporated in the data processing terms of the respective Processors identified at
            Article 9, which mechanisms include the Standard Contractual Clauses approved by the
            European Commission and, where applicable, the United Kingdom International Data Transfer
            Addendum.
          </p>
        </section>

        <section>
          <h2>17. Security</h2>
          <p>
            17.1 The Company implements technical and organisational measures appropriate to the nature
            and volume of the Personal Information processed, including encryption in transit and
            database access rules preventing any User from altering the records of another User or from
            writing to Application content.
          </p>
          <p>
            17.2 The daily completion times described at Section 4.2(i) are readable by authenticated
            instances of the Application for the purpose of comparative computation. Each such record
            comprises the Anonymous Identifier and an elapsed duration only, and contains no direct
            identifier.
          </p>
          <p>
            17.3 No method of transmission or storage is entirely secure. The Company does not warrant
            absolute security.
          </p>
        </section>

        <section>
          <h2>18. Amendment</h2>
          <p>
            18.1 The Company may amend this Privacy Policy from time to time. The Effective Date
            appearing above shall be revised upon any amendment.
          </p>
          <p>
            18.2 Where an amendment is material, including the introduction of a new category of Personal
            Information or a new purpose of processing, notice shall be given within the Application
            prior to the amendment taking effect.
          </p>
        </section>

        <section>
          <h2>19. Controller Particulars</h2>
          <p>
            tnysocial LLC<br />
            A Delaware limited liability company<br />
            State of Delaware, United States of America<br />
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>
          </p>
          <p>
            Reference is further made to the <Link to="/terms">Terms and Conditions</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
